import { forwardRef, useRef, useImperativeHandle, KeyboardEvent, ForwardRefRenderFunction } from "react";
import InputAreaAI from "components/InputAreaAI";
import { DataType } from "components/InputAreaAI/components/Assistant/data";
import { InputGroup, IconLabelGroup, Icon, Label } from "./styled";

interface PurposeInputFieldProps {
  icon: string;
  label: string;
  placeholder: string;
  value: string;
  type: DataType;
  goalData: {
    i: number;
    area: string;
    category: string;
    goal_name: string;
  };
  onChange?: (value: string, field: string, ai?: boolean) => void;
  onSave?: (value: string, field: string) => void;
  name?: string;
  nextInputRef?: React.RefObject<any>;
  disabled?: boolean;
}

interface InputMethods {
  focus: () => void;
  click: () => void;
  clearValue: () => void;
}

const PurposeInputField: ForwardRefRenderFunction<InputMethods, PurposeInputFieldProps> = (
  {
    icon,
    label,
    placeholder,
    value,
    type,
    goalData,
    onChange,
    onSave,
    name,
    nextInputRef,
    disabled
  },
  ref
) => {
  const inputRef = useRef<any>(null);
  
  // Use useImperativeHandle to expose methods to parent component
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current && inputRef.current.focus) {
        inputRef.current.focus();
      }
    },
    click: () => {
      if (inputRef.current && inputRef.current.click) {
        inputRef.current.click();
      }
    },
    clearValue: () => {
      if (inputRef.current && inputRef.current.clearValue) {
        inputRef.current.clearValue();
      }
    }
  }));
  
  const handleChange = (value: string, field: string, ai?: boolean) => {
    if (onChange) {
      onChange(value, field || name || '', ai);
    }
  };

  const handleBlur = (value: string, field: string) => {
    if (onSave) {
      onSave(value, field || name || '');
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Save changes
      if (onSave) {
        onSave((e.target as HTMLTextAreaElement).value, name || '');
      }
      
      // Focus on the next input field if provided
      if (nextInputRef && nextInputRef.current) {
        setTimeout(() => {
          if (nextInputRef.current && nextInputRef.current.focus) {
            nextInputRef.current.focus();
          } else if (nextInputRef.current && nextInputRef.current.click) {
            nextInputRef.current.click();
          }
        }, 0);
      }
    }
  };
  
  return (
    <InputGroup>
      <IconLabelGroup>
        <Icon src={icon} alt={`${label} icon`} />
        <Label>{label}</Label>
      </IconLabelGroup>
      <InputAreaAI
        id={`input-${type}`}
        size="sm"
        max={255}
        name={name}
        placeholder={placeholder}
        value={value}
        ref={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        hideSuggestButton={disabled}
        assistant={{
          type,
          title: label,
          order: (goalData.i + 1).toString(),
          area: goalData.area,
          category: goalData.category,
          goal: goalData.goal_name,
          prompt: value
        }}
      />
    </InputGroup>
  );
};

export default forwardRef(PurposeInputField);
